import { Fancybox } from "@fancyapps/ui/dist/fancybox/";
import "@fancyapps/ui/dist/fancybox/fancybox.css";
import { gsap } from "gsap";

import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { TextPlugin } from "gsap/TextPlugin";

gsap.registerPlugin(ScrollTrigger, SplitText, TextPlugin);

// Fancybox
Fancybox.bind("[data-fancybox]", {
  Carousel: {
    Panzoom: {
      panOnlyZoomed: true,
    },
    gestures: false,
  },
});

// -------------------------
// HERO
// -------------------------
function heroTimeline() {
  let hero = gsap.timeline({
    scrollTrigger: {
      trigger: "#__home__ .hero-video-wrapper",
      start: "-1 top",
      end: "bottom top",
      scrub: true,
      pin: true,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      snap: true,
    },
  });

  hero.fromTo(
    ".overlay-background",
    {
      autoAlpha: 0,
      y: 100,
      duration: 0,
      lazy: false,
    },
    { autoAlpha: 0.7 },
  );

  const headlines = gsap.utils.toArray(".headlines-wrapper h1");

  hero.fromTo(
    headlines,
    { autoAlpha: 0, duration: 0, lazy: false },
    { autoAlpha: 1 },
  );

  headlines.forEach((h) => {
    new SplitText(h, {
      type: "lines",
      autoSplit: true,
      mask: "lines",
      onSplit(self) {
        hero.from(
          self.lines,
          {
            y: 100,
            autoAlpha: 0,
            stagger: 2,
            duration: 5,
            ease: "expo.in",
            lazy: false,
          },
          "+=2.5",
        );
      },
    });
  });

  hero.to(".hero-video-wrapper", {
    borderColor: "#850000",
    ease: "expo.inOut",
  });
}

// -------------------------
// COPY BLOCKS
// -------------------------
function copyBlockTimeline() {
  let sections = gsap.utils.toArray("section.copy-block-section__slide");
  let tl = gsap.timeline();

  sections.forEach((section) => {
    let sectionSelector = gsap.utils.selector(section);
    let headline = sectionSelector("h5");
    let halftone = sectionSelector("img");

    const isText = section.classList.contains("white");

    let sectionTimeline;
    tl.add(sectionTimeline);

    if (
      halftone.length > 0 &&
      halftone[0].classList.contains("halftone-red-fadeout")
    ) {
      sectionTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          pin: false,
          start: "top top",
          snap: "labels",
        },
      });
    } else {
      sectionTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          pin: true,
          start: isText ? "center center" : "top top",
          scrub: true,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          snap: 1,
        },
      });
      tl.addLabel(section.classList[1]);
    }

    if (headline.length > 0) {
      SplitText.create(headline, {
        type: "lines",
        mask: "lines",
        autoSplit: true,
        onSplit(self) {
          sectionTimeline.from(
            self.lines,
            {
              y: 100,
              autoAlpha: 0,
              stagger: 0.15,
              lazy: false,
            },
            "+=0.5",
          );
        },
      });

      let headlineEmphasis = sectionSelector("h5 strong");
      if (headlineEmphasis.length > 0) {
        let currentSize = gsap.getProperty("strong", "fontSize");

        sectionTimeline.to(headlineEmphasis, {
          fontSize: currentSize * 3,
          ease: "expo.inOut",
          color: "var(--color-kelly-green)",
          fontWeight: 900,
        });
      }

      let headlineLink = sectionSelector(".contact-link h5 a");
      if (headlineLink.length > 0) {
        sectionTimeline.to(headlineLink, {
          ease: "expo.inOut",
          "--underlineWidth": "100%",
        });
      }
    }
  });
}

// -------------------------
// SKYLINE
// -------------------------
function skylineTimeline() {
  // 1. Ensure images are loaded before calculating
  const img = document.querySelector(".skyline img");

  if (!img.complete) {
    img.onload = () => skylineTimeline();
    return;
  }

  let wrapper = document.querySelector(".skyline__wrapper");

  // 2. Set height and refresh ScrollTrigger
  gsap.set(wrapper, { height: img.offsetHeight });

  let skyline = gsap.timeline({
    scrollTrigger: {
      trigger: wrapper,
      start: "center center",
      end: "+=1000",
      pin: true,
      scrub: true,
      pinSpacing: true,
      invalidateOnRefresh: true,
      snap: 1,
    },
  });

  skyline.to(".skyline .square", {
    borderRadius: "50%",
    stagger: { amount: 0.5, from: "random", grid: "auto" },
  });

  skyline.to(".skyline .square", {
    autoAlpha: 0,
    scale: 0,
    borderRadius: "50%",
    stagger: { amount: 3, from: "random", grid: "auto" },
  });
}

// -------------------------
// LINKS
// -------------------------
function linksTimeline() {
  const linkSections = gsap.utils.toArray(
    ".homepage-link-section-headline, .home-link-section, .show-all-work__wrapper",
  );

  linkSections.forEach((section) => {
    gsap.from(section, {
      autoAlpha: 0,
      y: 100,
      duration: 2,
      lazy: false,
      scrollTrigger: {
        trigger: section,
        start: "top bottom",
        end: "top center",
        scrub: true,
        invalidateOnRefresh: true,
      },
    });
  });
}

// -------------------------
// NAV MENU
// -------------------------
function navScrollAnimation() {
  const nav = document.querySelector(".navbar");
  const isTransparent = nav.classList.contains("transparent");

  const show = gsap
    .from(nav, {
      yPercent: -100,
      paused: true,
      duration: 0.2,
    })
    .progress(1);

  ScrollTrigger.create({
    start: "top top",
    end: 99999,
    onUpdate: (self) => {
      self.direction === -1 ? show.play() : show.reverse();

      if (isTransparent) {
        if (self.scroll() > 500) {
          nav.classList.remove("transparent");
        } else {
          nav.classList.add("transparent");
        }
      }
    },
  });
}

// function navHoverAnimation() {
//   const navLinks = gsap.utils.toArray(
//     ".navbar-link:not(.mobile-nav-button, .mobile-nav-link)",
//   );
//   const isTransparent = document
//     .querySelector(".navbar")
//     .classList.contains("transparent");
//   if (isTransparent) return;

//   navLinks.forEach((link) => {
//     let width = gsap.getProperty(link, "width", "px");

//     link.addEventListener("mouseenter", () => {
//       gsap.to(link, { "--navHoverUnderline": width });
//     });

//     link.addEventListener("mouseleave", () => {
//       gsap.to(link, { "--navHoverUnderline": "0%" });
//     });
//   });
// }

// -------------------------
// STATS
// -------------------------
function statsAnimation() {
  const stats = gsap.utils.toArray(".stats__stat-item");
  if (!stats.length) return;

  stats.forEach((stat) => {
    let number = gsap.utils.toArray(".stat__number", stat);

    gsap.from(
      number,
      {
        duration: 1,
        delay: 0.5,
        innerText: 0,
        snap: { innerText: 1 },
        ease: "expo.inOut",
        scrollTrigger: {
          trigger: stat,
          invalidateOnRefresh: true,
        },
      },
      0.5,
    );
  });
}

function mobileNavController() {
  let navButtonOpen = document.getElementById("mobile-nav-button_open");
  let navButtonClose = document.getElementById("mobile-nav-button_close");

  let menu = document.getElementsByClassName("navbar")[0];
  let mobileMenu = document.getElementsByClassName("mobile-nav")[0];

  navButtonOpen.addEventListener("click", () => {
    // hide horizontal menu
    // show full menu
    menu.classList.add("hide");
    mobileMenu.classList.remove("hide");
  });

  navButtonClose.addEventListener("click", () => {
    // show horizontal menu
    // hide full menu
    menu.classList.remove("hide");
    mobileMenu.classList.add("hide");
  });
}

function generateStructuredData() {
  const faqBlocks = document.querySelectorAll("details.dhd-faq");

  // 1. Build the structure required by Schema.org
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: Array.from(faqBlocks).map((block) => {
      return {
        "@type": "Question",
        name: block.querySelector(".dhd-faq__title h5")?.innerText.trim(),
        acceptedAnswer: {
          "@type": "Answer",
          text: block.querySelector(".dhd-faq__content")?.textContent.trim(),
        },
      };
    }),
  };

  // 2. Create the script element
  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.text = JSON.stringify(faqSchema);

  // 3. Inject it into the document head
  document.head.appendChild(script);

  console.log("✅ FAQ Schema injected successfully:", faqSchema);
}

// -------------------------
// INIT
// -------------------------
window.addEventListener("load", () => {
  let media = gsap.matchMedia();

  generateStructuredData();

  navScrollAnimation();
  statsAnimation();

  // media.add("(min-width: 768px)", () => {
  //   navHoverAnimation();
  // });

  media.add("(max-width: 768px)", () => {
    mobileNavController();
  });

  if (window.location.pathname.length > 1) return;

  media.add("(min-width: 1024px)", () => {
    // Set initial states (no animation)
    gsap.set(".overlay-background, .headlines-wrapper h1", {
      autoAlpha: 0,
      opacity: 0,
    });
    heroTimeline();
    copyBlockTimeline();
    skylineTimeline();
    linksTimeline();
  });

  // Force recalculation after everything is ready
  ScrollTrigger.refresh(true);
});
